import { fdaSupplementEffectiveDateError } from '@shared/fdaSubmissionVocabulary';
import { SubmissionWorkspaceLoading } from '@/components/SubmissionWorkspaceLoading';
import { FdaActivityReview } from '@/components/submission/FdaActivityReview';
import { SubmissionDeleteButton } from '@/components/submission/SubmissionDeleteButton';
import { SubmissionWorkspaceTabs } from '@/components/SubmissionWorkspaceTabs';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { v2, ApiError, type CompanyProfile, type Document, type Submission } from '@/api/v2Client';
import {
  getHcSequenceDescriptionsForType,
  HC_SEQUENCE_DESCRIPTIONS,
} from '@/constants/hc-sequence-descriptions';
import { SubmissionIdentityHeader } from '@/components/SubmissionHeroCard';
import { useStatusEvidence } from '@/hooks/useStatusEvidence';
import { subscribeSubmissionRefresh } from '@/lib/submissionRefresh';
import { EvidenceStatusPipeline } from '@/components/submission/EvidenceStatusPipeline';
import {
  isSequenceLocked,
  LOCKED_EDIT_TOOLTIP,
  normalizeStatus,
} from '@/constants/status';
import { Button } from '@/components/shared/Button';
import { buttonStyle } from '@/components/shared/buttonStyle';
import { EsgMonitorBanner } from '@/components/submission/EsgMonitorBanner';
import { CompileGateBanner } from '@/components/submission/CompileGateBanner';
import {
  SubmissionTabs,
  type SubmissionTabKey,
} from '@/components/submission/SubmissionTabs';
import { DocumentsTabPanel } from '@/components/submission/DocumentsTabPanel';
import { AdminFormsTabPanel } from '@/components/submission/AdminFormsTabPanel';
import { LifecycleTabPanel } from '@/components/submission/LifecycleTabPanel';
import { NodeExtensionManager } from '@/components/submission/NodeExtensionManager';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { UnlockDialog } from '@/components/submission/UnlockDialog';
import { formatApiError } from '@/lib/errors';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import type { UnlockKind } from '@/api/v2Client';
import { parseSubmissionTab } from '@/lib/submissionTab';

// phase-fix-submission-details-polish (review §2 #15) — FDA blocks
// <node-extension> outright (submission-node-extensions policy), so the tab
// would only ever be a dead end there. ONE constant feeds both the tab list
// and the ?tab= fallback (T-17 T17-D12).
const FDA_HIDDEN_TABS: readonly SubmissionTabKey[] = ['node-extensions'];

// Phase phase-fix-admin-forms-tab-crash — human label for the tab-level
// ErrorBoundary fallback ("Admin Forms couldn’t be displayed").
const TAB_LABELS: Record<SubmissionTabKey, string> = {
  documents: 'Documents',
  forms: 'Admin Forms',
  lifecycle: 'Lifecycle History',
  'node-extensions': 'Node Extensions',
};

const DETAIL_ERROR_MESSAGES = { PermissionDenied: 'Your role does not have permission for this action.', DatabaseUnavailable: 'The database is temporarily unavailable. Please try again.' };

// ISS-028 — the update/archive responses carry no ActiveUnlocks, so replacing
// submission state wholesale after a save HID an active grant: the Edit and
// Relock controls vanished and "Locked" reappeared until a manual refresh.
// When the response omits the field, keep the grants we already know are
// active (still the one evaluator — isSequenceLocked + the GET-supplied list;
// a grant/relock always refetches via reloadKey, so this can't go stale).
function mergeSavedSubmission(prev: Submission | null, updated: Submission): Submission {
  if (prev === null || updated.ActiveUnlocks !== undefined) return updated;
  return { ...updated, ActiveUnlocks: prev.ActiveUnlocks };
}

export function SubmissionDetailPage(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [error, setError] = useState<string | null>(null);
  // phase-ui-review-F16 — a deleted/missing submission (404) is not a transient
  // failure to retry, and the raw NotFound JSON meant nothing to a person. Flag
  // it so the error surface can read as "no longer available" with a way back,
  // while every other error keeps its message + Retry.
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // phase-admin-unlock
  const perms = useUserPermissions();
  const canWrite = !perms.loading && !perms.failed && perms.hasPermission('Submissions', 'write');
  const canUpload = !perms.loading && !perms.failed && perms.hasPermission('Documents', 'write');
  const canUnlock = perms.hasPermission('Submissions', 'unlock');
  const [unlockDialog, setUnlockDialog] = useState<
    { mode: 'grant' | 'relock'; initialKind?: UnlockKind } | null
  >(null);
  // T-17 (T17-D12) — the active tab is derived from `?tab=`, not local state,
  // so a deep link / reload / Back keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = parseSubmissionTab(searchParams.get('tab'));
  const setActiveTab = useCallback(
    (tab: SubmissionTabKey): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'documents') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Phase 5.10c — observed by DocumentsTabPanel via onDocumentsChanged.
  // Drives the compile-gate banner and hero counts. `null` until first load.
  const [docs, setDocs] = useState<readonly Document[] | null>(null);
  const statusEvidence = useStatusEvidence([id], submission, docs);
  useEffect(() => subscribeSubmissionRefresh(() => setReloadKey((key) => key + 1)), []);
  // Phase 7.4 — editable HC sequence description
  const [hcSeqDescEdit, setHcSeqDescEdit] = useState<string | null>(null);
  const [hcSeqDescSaving, setHcSeqDescSaving] = useState(false);
  const [hcSeqDescError, setHcSeqDescError] = useState<string | null>(null);

  // Phase UI-12 — Edit Submission modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDrugName, setEditDrugName] = useState('');
  const [editIndication, setEditIndication] = useState('');
  const [editSeqDesc, setEditSeqDesc] = useState('');
  const [editApplicant, setEditApplicant] = useState('');
  const [editSubmissionType, setEditSubmissionType] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // phase-missing-profile-visibility (item 4 — relink path) — the existing
  // Edit modal is the one Edit/Settings mechanism on this page; extended
  // with a CompanyProfileId picker rather than building a second modal. The
  // header flag (missing/never-linked) opens this same modal.
  const [editCompanyProfileId, setEditCompanyProfileId] = useState<string>('');
  const [companyProfileOptions, setCompanyProfileOptions] = useState<readonly CompanyProfile[] | null>(
    null,
  );
  const [companyProfileOptionsError, setCompanyProfileOptionsError] = useState<string | null>(null);

  const handleDocumentsChanged = useCallback((next: readonly Document[]): void => {
    setDocs(next);
  }, []);

  // T-17 (T17-D12) — the compile-gate counts are fed by DocumentsTabPanel's
  // fetch. A deep link straight to another tab (?tab=forms) never mounts that
  // panel, so the banner and hero counts would read an empty list.
  // Load the list here in that case (the panel keeps feeding it afterwards).
  const landedOffDocuments = requestedTab !== null && requestedTab !== 'documents';
  useEffect(() => {
    if (!id || !landedOffDocuments || docs !== null) return;
    let cancelled = false;
    v2.documents
      .listBySubmission(id)
      .then((list) => {
        if (!cancelled) setDocs((prev) => prev ?? list);
      })
      .catch(() => {
        /* the gate stays "unknown"; the Documents tab surfaces the real error */
      });
    return () => {
      cancelled = true;
    };
  }, [id, landedOffDocuments, docs]);

  // Phase 5.10c — compile-gate counts. Must live above the early returns
  // so the hook order stays stable across renders (rules-of-hooks).
  const gate = useMemo(() => {
    const list = docs ?? [];
    const classifiedTotal = list.filter((d) => Boolean(d.Classification)).length;
    const confirmed = list.filter((d) => Boolean(d.ConfirmedAt)).length;
    const unconfirmedClassifiedIds = list
      .filter((d) => Boolean(d.Classification) && !d.ConfirmedAt)
      .map((d) => d.Id);
    return {
      totalDocs: list.length,
      classifiedDocs: classifiedTotal,
      confirmedDocs: confirmed,
      unconfirmedClassifiedIds,
    };
  }, [docs]);
  useEffect(() => {
    if (!id) return;
    setSubmission(null);
    setError(null);
    setNotFound(false);
    v2.submissions
      .get(id)
      .then(setSubmission)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? formatApiError(err, 'Failed to load submission', DETAIL_ERROR_MESSAGES) : 'Failed to load submission');
      });
  }, [id, reloadKey]);

  const openEditModal = (): void => {
    if (!submission || !canWrite) return;
    setEditDrugName(submission.DrugName ?? '');
    setEditIndication(submission.Indication ?? '');
    setEditSeqDesc(submission.SequenceDescription ?? '');
    setEditApplicant(submission.Applicant ?? '');
    setEditSubmissionType(submission.SubmissionType ?? '');
    setEditCompanyProfileId(submission.CompanyProfileId ?? '');
    setEditError(null);
    setEditModalOpen(true);
    // phase-missing-profile-visibility — load the picker's options lazily,
    // only when the modal actually opens (not on every page load).
    if (companyProfileOptions === null) {
      setCompanyProfileOptionsError(null);
      v2.companyProfiles
        .list()
        .then(setCompanyProfileOptions)
        .catch((err: unknown) => {
          setCompanyProfileOptionsError(
            err instanceof ApiError ? formatApiError(err, 'Failed to load company profiles', DETAIL_ERROR_MESSAGES) : 'Failed to load company profiles',
          );
        });
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!submission || !canWrite) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await v2.submissions.update(id, {
        DrugName: editDrugName,
        Indication: editIndication || null,
        SequenceDescription: editSeqDesc,
        Applicant: editApplicant || null,
        SubmissionType: editSubmissionType,
        CompanyProfileId: editCompanyProfileId || null,
      });
      // The update response omits derived grants; reload the full GET view.
      setReloadKey((key) => key + 1);
      setEditModalOpen(false);
    } catch (err) {
      setEditError(err instanceof ApiError ? formatApiError(err, 'Failed to save', DETAIL_ERROR_MESSAGES) : 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const handleSaveHcSeqDesc = async (): Promise<void> => {
    if (!submission || !canWrite || hcSeqDescEdit === null) return;
    setHcSeqDescSaving(true);
    setHcSeqDescError(null);
    try {
      await v2.submissions.update(id, { SequenceDescription: hcSeqDescEdit });
      // The update response omits derived grants; reload the full GET view.
      setReloadKey((key) => key + 1);
      setHcSeqDescEdit(null);
    } catch (err) {
      setHcSeqDescError(err instanceof ApiError ? formatApiError(err, 'Failed to save', DETAIL_ERROR_MESSAGES) : 'Failed to save');
    } finally {
      setHcSeqDescSaving(false);
    }
  };

  // phase-ui-review-F16 — a deleted/missing submission reads as plainly
  // unavailable with a way back, not as the raw NotFound JSON. No Retry: the
  // record is gone, so retrying the same id just 404s again.
  if (notFound) {
    return (
      <section data-testid="submission-not-found">
        <Link to="/home" style={{ color: 'var(--accent)' }}>
          ← Back to submissions
        </Link>
        <div role="alert" style={{ marginTop: 12 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>This submission is no longer available.</p>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
            It may have been deleted. Return to the submissions list to find your other work.
          </p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <Link to="/home" style={{ color: 'var(--accent)' }}>
          ← Back to submissions
        </Link>
        <div role="alert" style={{ color: 'var(--red)', marginTop: 12 }}>
          {error}
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  if (!submission) {
    return (
      <section>
        <SubmissionWorkspaceLoading submissionId={id ?? ''} active="details" />
      </section>
    );
  }

  // V2 doesn't yet expose AgencyReferenceNumber on the typed Submission
  // shape — pull it loosely through the index signature. The banner
  // hides when this field is absent or empty.
  const agencyRef = (submission as Record<string, unknown>)['AgencyReferenceNumber'];
  // T-17 (T17-D12) — a hidden-but-valid ?tab= (node-extensions on FDA) falls
  // back to Documents WITHOUT rewriting the URL, and says why.
  const hiddenTabs: readonly SubmissionTabKey[] =
    submission.Region === 'FDA' ? FDA_HIDDEN_TABS : [];
  const requestedTabHidden = requestedTab !== null && hiddenTabs.includes(requestedTab);
  const activeTab: SubmissionTabKey =
    requestedTab !== null && !requestedTabHidden ? requestedTab : 'documents';
  const showEsg = typeof agencyRef === 'string' && agencyRef.length > 0;
  const submittedAtRaw = (submission as Record<string, unknown>)['SubmittedAt'];
  const submittedAt = typeof submittedAtRaw === 'string' ? submittedAtRaw : null;

  // phase-submission-detail-v1-parity — V1 derives the workflow banners +
  // in-card action row from the normalised status. Mirror that here.
  const st = normalizeStatus(submission.Status);
  const isComplete = st === 'Complete';
  // phase-fix-submission-status-truth (§3.4) — locked-sequence integrity.
  const sequenceLocked = isSequenceLocked(submission);
  // phase-admin-unlock — active grants, if any. The banner and the Edit-gate
  // exception both read from the same GET-supplied list (one evaluator: the
  // server's getActiveUnlocks — this is just display of that server truth).
  const activeUnlocks = submission.ActiveUnlocks ?? [];
  const activeEditUnlock = activeUnlocks.find((g) => g.Kind === 'edit') ?? null;

  // Phase KB-submission-types-v3 — build a human-readable submission type label
  // from the catalog codes when available. Falls back to the raw SubmissionType.
  const formattedTypeLabel = (() => {
    const appType = submission.SubmissionType ?? null;
    const typeCode = submission.SubmissionTypeCode ?? null;
    const subTypeCode = submission.SubmissionSubTypeCode ?? null;
    if (!appType) return null;
    if (!typeCode) return appType;
    // e.g. "NDA — Original Application" or "NDA — Supplement — CBE-30 / Manufacturing"
    const typePart = typeCode; // Could be enriched with catalog name in future
    if (subTypeCode) return `${appType} — ${typePart} / ${subTypeCode}`;
    return `${appType} — ${typePart}`;
  })();

  return (
    // phase-documents-tab-layout-batch (Item 5) — page goes full-width via
    // .page--wide (33b55a2); the 1400px cap left ~3in of right dead space at
    // 1440px+. Removed so the table+preview split reaches the side padding.
    <section>
      <SubmissionWorkspaceTabs
        submissionId={id}
        name={submission.DrugName}
        sequence={submission.SequenceNumber}
        header={<SubmissionIdentityHeader submission={submission} statusEvidence={statusEvidence.get(id)} submissionTypeLabel={formattedTypeLabel ?? undefined} showSequence onRelinkProfileClick={canWrite ? openEditModal : undefined} headerActions={
            /* Phase phase-ui-consistency-sweep (§3B) — Edit + settings gear
               now live in the hero card's headerActions slot (placed
               consistently in the navy bar) instead of floating absolutely
               on top of it. The Edit button uses the shared Button so it
               matches every other screen (§3C). */
            <>
              {/* phase-fix-submission-status-truth (§3.4) — a locked sequence
                  hides its Edit affordance; the server rejects edits too.
                  phase-admin-unlock — UNLESS an active unlock-to-edit grant
                  makes editing permitted again (server-enforced too). */}
              {canWrite && (!sequenceLocked || activeEditUnlock !== null) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openEditModal}
                  data-testid="edit-submission-btn"
                  title="Edit submission metadata"
                >
                  ✎ Edit
                </Button>
              )}
              <SubmissionDeleteButton submissionId={id} name={submission.DrugName} sequence={submission.SequenceNumber} />
              {/* phase-admin-unlock — the privileged, permission-gated escape
                  hatch. Only rendered for a user who actually holds
                  Submissions:unlock (avoids a guaranteed-403 button). */}
              {canUnlock && sequenceLocked && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setUnlockDialog({ mode: 'grant' })}
                  data-testid="open-unlock-dialog-btn"
                  title="Unlock this sequence for editing or renumbering — requires a reason, fully audited"
                >
                  🔓 Unlock…
                </Button>
              )}
              {submission.IsArchived === true && (
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="unhide-submission-btn"
                  title="Unhide this sequence — it returns to the default dashboard view"
                  onClick={() => {
                    void v2.submissions
                      .archive(id, false)
                      .then((updated) => setSubmission((prev) => mergeSavedSubmission(prev, updated)))
                      .catch(() => setReloadKey((k) => k + 1));
                  }}
                >
                  Unhide
                </Button>
              )}
              {sequenceLocked && activeEditUnlock === null && (
                <span
                  data-testid="edit-submission-locked"
                  title={LOCKED_EDIT_TOOLTIP}
                  aria-label={LOCKED_EDIT_TOOLTIP}
                  style={{
                    // phase-ui-hero-right-metadata-contrast — explicit light
                    // text; the old opacity-on-inherited-dark was invisible
                    // on the navy band.
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🔒 Locked
                </span>
              )}
              {/* phase-fix-submission-details-polish (review §2 #13) — the
                  gear was icon-only (title-attr only). Visible text + matching
                  aria-label. */}
              <Link
                to={`/admin/settings?scope=submission&submissionId=${id}`}
                data-testid="submission-settings-link"
                title="Submission settings"
                aria-label="Submission settings"
                style={buttonStyle('ghost-on-dark', 'sm')}
              >
                ⚙ Settings
              </Link>
            </>
          } />}
        active="details"
        hasUnsavedChanges={(hcSeqDescEdit !== null && hcSeqDescEdit !== (submission.SequenceDescription ?? '')) || (editModalOpen && (editDrugName !== (submission.DrugName ?? '') || editIndication !== (submission.Indication ?? '') || editSeqDesc !== (submission.SequenceDescription ?? '') || editApplicant !== (submission.Applicant ?? '') || editSubmissionType !== (submission.SubmissionType ?? '') || editCompanyProfileId !== (submission.CompanyProfileId ?? '')))}
      />
      <div style={{ marginTop: 12 }}>
        {submission.Region === 'FDA' && (submission.FdaActivityMetadataNeedsReview || !submission.FdaActivityStartSequence || fdaSupplementEffectiveDateError(submission.SubmissionTypeCode ?? '', submission.SubmissionSubTypeCode ?? '', submission.SupplementEffectiveDateType)) && (
          <FdaActivityReview key={submission.Id} submission={submission}
            canEdit={canWrite && (!sequenceLocked || activeEditUnlock !== null)}
            onSaved={updated => setSubmission(previous => mergeSavedSubmission(previous, updated))} />
        )}
          {/* Complete-lock banner (V1: padded green strip with a lock). */}
          {isComplete && (
            <div
              data-testid="complete-lock-banner"
              style={{
                marginTop: 12,
                padding: '8px 14px',
                background: 'var(--green-bg, #ECFDF5)',
                border: '1px solid #BBF7D0',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--green, #15803d)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>🔒</span> This submission is complete and locked.
              Create a new sequence to make changes.
            </div>
          )}

          {/* phase-admin-unlock — the mandatory "unlocked, by whom, and why"
              banner (ratified design: the state is never silent). Renders
              once per active grant — a sequence can hold both an edit and a
              renumber grant at once, separately. Amber, not green: this is a
              controlled EXCEPTION on top of a still-locked sequence, not a
              return to a normal editable state. */}
          {activeUnlocks.map((grant) => (
            <div
              key={grant.Id}
              data-testid={`unlocked-banner-${grant.Kind}`}
              style={{
                marginTop: 12,
                padding: '8px 14px',
                background: 'var(--amber-bg, #FFFBEB)',
                border: '1px solid #FDE68A',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--amber, #92400E)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 16 }}>🔓</span>
              <span>
                Unlocked {grant.Kind === 'edit' ? 'for editing' : 'for renumbering'} by{' '}
                <strong>{grant.UnlockedByEmail ?? grant.UnlockedByUserId}</strong> — {grant.Reason}
              </span>
              {canUnlock && (
                <button
                  type="button"
                  onClick={() => setUnlockDialog({ mode: 'relock', initialKind: grant.Kind })}
                  data-testid={`relock-btn-${grant.Kind}`}
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: '1px solid #92400E',
                    color: '#92400E',
                    borderRadius: 4,
                    padding: '2px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Relock
                </button>
              )}
            </div>
          ))}

          {/* Shared progress indicators, also shown on Compiler. */}
          <EvidenceStatusPipeline submissionId={id} evidence={statusEvidence.get(id)} onRefresh={statusEvidence.refresh} />

      </div>

      {/* Phase UI-12 — Edit Submission modal */}
      {canWrite && editModalOpen && (
        /* phase-movable-dialogs — shared movable shell. */
        <MovableDialog
          persistKey="submissionDetail.edit"
          title="Edit Submission"
          ariaLabel="Edit submission"
          testId="edit-submission-modal"
          onClose={() => setEditModalOpen(false)}
          closeOnBackdrop
          width="min(520px, 90vw)"
          zIndex={1000}
        >
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Drug name
                <input
                  type="text"
                  value={editDrugName}
                  onChange={(e) => setEditDrugName(e.target.value)}
                  data-testid="edit-drug-name"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Indication
                <input
                  type="text"
                  value={editIndication}
                  onChange={(e) => setEditIndication(e.target.value)}
                  data-testid="edit-indication"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Sequence description
                <input
                  type="text"
                  value={editSeqDesc}
                  onChange={(e) => setEditSeqDesc(e.target.value)}
                  data-testid="edit-seq-desc"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Applicant
                <input
                  type="text"
                  value={editApplicant}
                  onChange={(e) => setEditApplicant(e.target.value)}
                  data-testid="edit-applicant"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Submission type
                <input
                  type="text"
                  value={editSubmissionType}
                  onChange={(e) => setEditSubmissionType(e.target.value)}
                  data-testid="edit-submission-type"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              {/* phase-missing-profile-visibility (item 4) — the relink
                  path the header flag opens. Options come from the same
                  active-profiles list the delete/usage screen uses. */}
              <label style={{ fontSize: 13 }}>
                Company profile
                <select
                  data-testid="edit-company-profile"
                  value={editCompanyProfileId}
                  onChange={(e) => setEditCompanyProfileId(e.target.value)}
                  disabled={companyProfileOptions === null && !companyProfileOptionsError}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' }}
                >
                  {/* ISS-020 — generation REFUSES (409 NoLinkedCompanyProfileError,
                      D-074) when no profile is linked; no tenant default is
                      substituted. The empty option must say so, not promise a
                      fallback the gate will never apply. */}
                  <option value="">— none linked (required: an explicit link before generation) —</option>
                  {/* Degenerate option so a currently-linked-but-DELETED profile
                      (the exact "missing" state this phase surfaces) doesn't
                      silently collapse the select to blank on open — the
                      operator sees plainly what's set and that it's broken. */}
                  {editCompanyProfileId &&
                    !(companyProfileOptions ?? []).some((p) => p.Id === editCompanyProfileId) && (
                      <option value={editCompanyProfileId}>
                        {`(missing/deleted profile ${editCompanyProfileId})`}
                      </option>
                    )}
                  {(companyProfileOptions ?? []).map((p) => (
                    <option key={p.Id} value={p.Id}>
                      {p.CompanyName}
                    </option>
                  ))}
                </select>
                {companyProfileOptionsError && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Could not load company profiles ({companyProfileOptionsError}).
                  </span>
                )}
              </label>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <strong>NDA Number:</strong> {(submission as Record<string, unknown>)['NdaNumber'] as string ?? '—'} (read-only)
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <strong>Region:</strong> {submission.Region} (read-only)
              </div>
              {editError && (
                <div role="alert" style={{ color: 'var(--red)', fontSize: 12 }}>{editError}</div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button
                  variant="ghost"
                  onClick={() => setEditModalOpen(false)}
                  disabled={editSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => { void handleSaveEdit(); }}
                  disabled={editSaving}
                  data-testid="edit-submission-save"
                >
                  {editSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </>
        </MovableDialog>
      )}

      {/* phase-admin-unlock — grant/relock dialog, shared component for both directions. */}
      {unlockDialog && (
        <UnlockDialog
          submissionId={id}
          mode={unlockDialog.mode}
          {...(unlockDialog.initialKind !== undefined && { initialKind: unlockDialog.initialKind })}
          onClose={() => setUnlockDialog(null)}
          onDone={() => {
            setUnlockDialog(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {/* TODO-104 — pass the full submission so the banner can derive the
          4 ACK states from live EsgSubmittedAt / EsgAckReceivedAt /
          EsgStaleAt / EsgAckPayload fields. The legacy positional props
          remain for the agency-reference-number display + as a fallback
          for the submittedAt when EsgSubmittedAt isn't populated yet. */}
      {showEsg && (
        <EsgMonitorBanner
          submission={submission}
          agencyReferenceNumber={agencyRef as string}
          submittedAt={submittedAt}
        />
      )}

      {/* Phase 7.4 — HC sequence description editor (only for Health Canada submissions) */}
      {submission.Region === 'Health Canada' && (
        <div
          data-testid="hc-seq-desc-panel"
          style={{
            margin: '12px 0',
            padding: '12px 16px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 13, minWidth: 180 }}>
              HC Sequence Description
            </span>
            {hcSeqDescEdit !== null ? (
              <>
                <select
                  value={hcSeqDescEdit}
                  onChange={(e) => setHcSeqDescEdit(e.target.value)}
                  disabled={hcSeqDescSaving}
                  data-testid="hc-seq-desc-select"
                  style={{
                    flex: 1,
                    minWidth: 200,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    fontSize: 13,
                  }}
                >
                  <option value="">— Select sequence description —</option>
                  {(submission.SubmissionType
                    ? getHcSequenceDescriptionsForType(submission.SubmissionType)
                    : HC_SEQUENCE_DESCRIPTIONS
                  ).map((d) => (
                    <option key={d.code} value={d.label}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void handleSaveHcSeqDesc()}
                  disabled={hcSeqDescSaving}
                  style={{
                    padding: '6px 14px',
                    background: 'var(--accent)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {hcSeqDescSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHcSeqDescEdit(null);
                    setHcSeqDescError(null);
                  }}
                  disabled={hcSeqDescSaving}
                  style={{
                    padding: '6px 14px',
                    background: '#fff',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  Cancel
                </button>
                {hcSeqDescError && (
                  <span style={{ color: 'var(--red)', fontSize: 12 }}>{hcSeqDescError}</span>
                )}
              </>
            ) : (
              <>
                <span
                  style={{ flex: 1, fontSize: 13, color: submission.SequenceDescription ? 'inherit' : 'var(--text-muted)' }}
                  data-testid="hc-seq-desc-value"
                >
                  {submission.SequenceDescription ?? '(not set)'}
                </span>
                <button
                  type="button"
                  disabled={!canWrite}
                  onClick={() => setHcSeqDescEdit(submission.SequenceDescription ?? '')}
                  style={{
                    padding: '4px 12px',
                    background: '#fff',
                    color: 'var(--accent)',
                    border: '1px solid var(--accent)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* phase-fix-locked-sequence-document-ui — a locked (submitted/
          completed) sequence is not heading to compile; the "not ready to
          compile" coaching banner would contradict the read-only state the
          page now enforces. Suppressed when locked. */}
      {!sequenceLocked && (
        <CompileGateBanner
          totalDocs={gate.totalDocs}
          classifiedDocs={gate.classifiedDocs}
          confirmedDocs={gate.confirmedDocs}
          unconfirmedClassifiedIds={gate.unconfirmedClassifiedIds}
          onGoToDocuments={() => setActiveTab('documents')}
        />
      )}

      {canUpload && !sequenceLocked && (
        <div style={{ display: 'flex', margin: '8px 0 20px 0' }}>
          <Link
            to={`/submissions/${id}/upload`}
            style={{
              padding: '8px 14px',
              background: 'var(--accent)',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            + Add document
          </Link>
        </div>
      )}

      {/* phase-fix-submission-details-polish (review §2 #15) — FDA blocks
          <node-extension> outright (submission-node-extensions policy), so
          the tab would only ever be a dead end there. */}
      {requestedTabHidden && (
        <div
          data-testid="submission-tab-hidden-note"
          role="status"
          style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}
        >
          Node Extensions isn&apos;t available for FDA submissions.
        </div>
      )}
      <SubmissionTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        hiddenTabs={hiddenTabs}
      />

      <div
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        style={{
          border: '1px solid var(--border)',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          background: 'var(--surface)',
          minHeight: 240,
        }}
      >
        {/* Phase phase-fix-admin-forms-tab-crash (§4.3) — a render error in one
            tab used to take the whole page down to React Router's raw error
            screen. Each panel now renders inside an ErrorBoundary, so a
            failure degrades to an inline card and the tab bar keeps working.
            `resetKey` clears the error when the user switches tabs. */}
        <ErrorBoundary label={TAB_LABELS[activeTab]} resetKey={activeTab}>
          {activeTab === 'documents' && (
            <DocumentsTabPanel
              submission={submission}
              onDocumentsChanged={handleDocumentsChanged}
              // phase-fix-locked-sequence-document-ui — the FULL lock ladder
              // (IsLocked flag or any post-compile status), not just Complete:
              // a Submitted/Approved sequence must not offer actions the
              // server will 403.
              isLocked={sequenceLocked}
            />
          )}
          {activeTab === 'forms' && (
            <AdminFormsTabPanel submission={submission} documents={docs} isLocked={sequenceLocked} />
          )}
          {activeTab === 'lifecycle' && <LifecycleTabPanel submissionId={id} />}
          {/* Phase node-extension-compiler-ui (TODO-048 Part B) */}
          {activeTab === 'node-extensions' && (
            <NodeExtensionManager
              submissionId={id}
              region={submission.Region ?? ''}
            />
          )}
        </ErrorBoundary>
      </div>

    </section>
  );
}
